import { z } from 'zod';

import { buildQueryString } from '../../utils/querystring';
import { makeEndpoint } from '../common';

export enum FieldType {
  INPUT = 'input',
  TEXTAREA = 'textarea',
  MULTIPLE_SELECT = 'multiple_select',
  SINGLE_SELECT = 'single_select',
  TERMS_AND_CONDITIONS = 'terms_and_conditions',
}

export const OptionSchema = z.object({
  key: z.string().uuid(),
  value: z.string(),
});
export type Option = z.infer<typeof OptionSchema>;

export enum CustomQuestionScope {
  PURCHASER = 'purchaser',
  PARTICIPANT = 'participant',
}

export enum ConditionMatchType {
  ANY = 'any',
  ALL = 'all',
}

export enum ConditionRule {
  VALUE_MATCHES = 'value_matches',
}

export const ConditionSchema = z.object({
  rule: z.nativeEnum(ConditionRule),
  conditionKey: z.string().uuid(),
  conditionValue: z.string().uuid(),
});
export type Condition = z.infer<typeof ConditionSchema>;

export const ConfigSchema = z.object({
  isRequired: z.boolean(),
  scope: z.nativeEnum(CustomQuestionScope),
  conditionsMatchType: z.nativeEnum(ConditionMatchType).nullable(),
  isActive: z.boolean(),
  conditions: z.array(ConditionSchema).nullable(),
  order: z.number(),
  isDefault: z.boolean().optional(),
  disabled: z.boolean().optional(),
});
export type Config = z.infer<typeof ConfigSchema>;

export const TermAndConditionSchema = z.object({
  label: z.string(),
  mediaId: z.string().uuid(),
  mediaURL: z.string(),
  isOverrided: z.boolean(),
});

export type TermAndCondition = z.infer<typeof TermAndConditionSchema>;

export const CustomQuestionSchema = z.object({
  id: z.string().uuid(),
  prompt: z.string(),
  fieldType: z.nativeEnum(FieldType),
  options: z.array(OptionSchema).nullable(),
  termsAndConditions: TermAndConditionSchema.nullable(),
  config: ConfigSchema,
});

export type CustomQuestion = z.infer<typeof CustomQuestionSchema>;

export const GetCustomQuestionsPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    experienceId,
    query,
  }: {
    brandId: string;
    experienceId: string;
    query: { isActive?: boolean; language?: string; includeDefault?: boolean };
  }) => `v1/brands/${brandId}/experiences/${experienceId}/custom-questions?${buildQueryString(query)}`,
  responseSchema: z.object({ items: z.array(CustomQuestionSchema) }),
  querySchema: z.object({
    isActive: z.boolean().optional(),
    language: z.string().optional(),
    includeDefault: z.boolean().optional(),
  }),
  requestSchema: z.void(),
});
